import { FileText, Award, Globe, FileCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LocalizedLink } from "@/components/LocalizedLink";

const getServices = (t: (key: string, fallback?: string) => string) => [
  {
    name: t("sluzby.apostila.name", "Apostila"),
    description: t("sluzby.apostila.description", "Overenie dokumentu pre použitie v krajinách Haagskej konvencie. Zabezpečíme apostilu na vaše slovenské dokumenty."),
    href: "/sluzby/apostila",
    icon: Award,
    gradient: "from-amber-500 to-orange-500",
    bgGradient: "from-amber-500/10 via-amber-500/5 to-transparent",
    features: [
      t("sluzby.apostila.feature1", "120+ krajín"),
      t("sluzby.apostila.feature2", "Kompletný servis"),
      t("sluzby.apostila.feature3", "Vybavenie do 5 dní"),
    ],
  },
  {
    name: t("sluzby.superlegalizacia.name", "Superlegalizácia"),
    description: t("sluzby.superlegalizacia.description", "Overenie pre krajiny mimo Haagskej konvencie. Kompletný servis vrátane overovania na MZV a veľvyslanectvách."),
    href: "/sluzby/superlegalizacia",
    icon: Globe,
    gradient: "from-purple-500 to-violet-600",
    bgGradient: "from-purple-500/10 via-purple-500/5 to-transparent",
    features: [
      t("sluzby.superlegalizacia.feature1", "Všetky krajiny"),
      t("sluzby.superlegalizacia.feature2", "MZV + veľvyslanectvá"),
      t("sluzby.superlegalizacia.feature3", "Osobné vybavenie"),
    ],
  },
  {
    name: t("sluzby.zahranicne_dokumenty.name", "Zahraničné dokumenty"),
    description: t("sluzby.zahranicne_dokumenty.description", "Potrebujete použiť zahraničný dokument na Slovensku? Zabezpečíme apostilu alebo superlegalizáciu z krajiny vydania."),
    href: "/sluzby/zahranicne-dokumenty",
    icon: FileCheck,
    gradient: "from-teal-500 to-emerald-500",
    bgGradient: "from-teal-500/10 via-teal-500/5 to-transparent",
    features: [
      t("sluzby.zahranicne_dokumenty.feature1", "Dokumenty zo zahraničia"),
      t("sluzby.zahranicne_dokumenty.feature2", "Úradný preklad"),
      t("sluzby.zahranicne_dokumenty.feature3", "Kompletný proces"),
    ],
  },
  {
    name: t("sluzby.uradne_preklady.name", "Úradné preklady"),
    description: t("sluzby.uradne_preklady.description", "Úradné a súdne preklady dokumentov do viac ako 30 jazykov. Rýchlo, spoľahlivo a s pečiatkou súdneho prekladateľa."),
    href: "/sluzby/uradne-preklady",
    icon: FileText,
    gradient: "from-blue-500 to-blue-600",
    bgGradient: "from-blue-500/10 via-blue-500/5 to-transparent",
    features: [
      t("sluzby.uradne_preklady.feature1", "30+ jazykov"),
      t("sluzby.uradne_preklady.feature2", "Express do 24h"),
      t("sluzby.uradne_preklady.feature3", "Pečiatka prekladateľa"),
    ],
  },
];

export function ServicesSection() {
  const { t } = useLanguage();
  const services = getServices(t);

  return (
    <section className="py-24 bg-background" aria-labelledby="services-heading">
      <div className="container mx-auto px-4">
        {/* Header */}
        <header className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-secondary font-semibold text-sm uppercase tracking-wider mb-4">
            {t("services.badge", "Čo ponúkame")}
          </span>
          <h2 id="services-heading" className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            {t("services.title", "Preklady a legalizácia dokumentov")}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t("services.subtitle", "Kompletné služby úradných prekladov a legalizácie pre súkromné osoby aj firmy")}
          </p>
        </header>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <LocalizedLink
              key={service.href}
              to={service.href}
              className="group relative block overflow-hidden rounded-3xl bg-card border border-border hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-1"
            >
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              {/* Top Gradient Accent Line */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
              
              <div className="relative p-8 md:p-10">
                {/* Icon */}
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} text-white shadow-lg mb-6 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}>
                  <service.icon className="w-7 h-7" />
                </div>
                
                {/* Content */}
                <h3 className="text-2xl font-display font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {service.name}
                </h3>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {service.description}
                </p>
                
                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.features.map((feature) => (
                    <span 
                      key={feature}
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-secondary" />
                      {feature}
                    </span>
                  ))}
                </div>
                
                {/* CTA */}
                <div className="flex items-center text-primary font-semibold group-hover:text-secondary transition-colors">
                  <span>{t("common.learn_more", "Zistiť viac")}</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </div>
            </LocalizedLink>
          ))}
        </div>
      </div>
    </section>
  );
}
